import {
  Drawer,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Collapse,
  Avatar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AssignmentIcon from "@mui/icons-material/Assignment";
import EventIcon from "@mui/icons-material/Event";
import GradeIcon from "@mui/icons-material/Grade";

import { useAuth } from "../../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { SyntheticEvent, useState } from "react";

const drawerWidth = 240;

export default function Sidebar() {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  const role = localStorage.getItem("role");
  const usnNumber = localStorage.getItem("usn_number");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // collapsible menus
  const [openExam, setOpenExam] = useState(true);

  /** ------------------------------
   * Menu Definitions
   --------------------------------*/
  const facultyMenu = [
    { text: "Exams", icon: <DashboardIcon />, path: "/faculty/facultyExams" },
  ];

  const studentMenu = [
    { text: "My exams", icon: <AssignmentIcon />, path: "/student/myexam" },
    { text: "Result", icon: <GradeIcon />, path: "/student/result" },
  ];

  const activeMenu = role === "Student" ? studentMenu : facultyMenu;

  const mobileNavItems = [
    ...activeMenu.map(({ text, icon, path }) => ({
      label: text,
      icon,
      value: path,
    })),
    { label: "Logout", icon: <LogoutIcon />, value: "logout" },
  ];

  const activeMobileValue =
    mobileNavItems.find((item) => location.pathname.startsWith(item.value))
      ?.value ?? "";

  const handleMenuCollapse = () => setOpenExam(!openExam);

  const handleMobileChange = (_: SyntheticEvent, newValue: string) => {
    if (newValue === "logout") {
      logout();
      return;
    }
    navigate(newValue);
  };

  /** ------------------------------
   * Render Menu List Items (Desktop)
   --------------------------------*/
  const renderMenuItems = () =>
    activeMenu.map(({ text, icon, path }) => {
      const isActive = location.pathname === path;
      return (
        <ListItemButton key={text} sx={{ pl: 4 }} onClick={() => navigate(path)}>
          <ListItemIcon sx={{ color: isActive ? "#FFC923" : "white" }}>
            {icon}
          </ListItemIcon>
          <ListItemText primary={text} />
        </ListItemButton>
      );
    });

  /** ------------------------------
   * Mobile Version
   --------------------------------*/
  if (isMobile) {
    return (
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
          zIndex: (theme) => theme.zIndex.drawer,
        }}
      >
        <BottomNavigation
          showLabels
          value={activeMobileValue}
          onChange={handleMobileChange}
          sx={{
            "& .MuiBottomNavigationAction-root": {
              color: (theme) => theme.palette.text.secondary,
              mt: 1.5,
              mb: 1,
            },
            "& .Mui-selected": { color: "#FFC923" },
          }}
        >
          {mobileNavItems.map(({ label, icon, value }) => (
            <BottomNavigationAction
              key={value}
              label={label}
              icon={icon}
              value={value}
            />
          ))}
        </BottomNavigation>
      </Box>
    );
  }

  /** ------------------------------
   * Desktop Version
   --------------------------------*/
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          backgroundColor: theme.palette.background.sidebar,
          height: `calc(100vh - 36px)`,
          margin: "16px",
          color: "white",
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        },
      }}
    >
      <Box>
        <Toolbar />

            {/* Profile section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 2,
              }}
            >
          <Avatar
                src={'data:image/jpeg;base64,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'} // replace with your image path

            alt="Profile"
            sx={{
              width: 100,
              height: 100,
              border: "3px solid #FFC923",
              mb: 1,
            }}
          />

          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {localStorage.getItem("contact_name")}
          </Typography>

          <Typography variant="body2" sx={{ color: "gray" }}>
            {usnNumber && usnNumber !== "null" ? usnNumber : ""}
          </Typography>
        </Box>

        {/* Navigation */}
        <List>
          <ListItemButton onClick={handleMenuCollapse}>
            <ListItemIcon sx={{ color: "white" }}>
              <SchoolIcon />
            </ListItemIcon>
            <ListItemText primary="Examination" />
            {openExam ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={openExam} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {renderMenuItems()}
            </List>
          </Collapse>
        </List>
      </Box>

      {/* Logout */}
      <Box>
        <List>
          <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              logout();
            }}
          >
              <ListItemIcon sx={{ color: "#FFC923" }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );
}
